import { getHexTileMapKey } from '../model/keys';
import type { HexMapProperties, HexTileProperties } from '../model/types';
import type { GenerateHexMapInput, HexMapGenerator } from './types';
import { createRandomGenerator } from './utils/random';

const DEFAULT_BASE_TILE: Omit<HexTileProperties, 'coordinates'> = {
  type: 'default',
  resourceValue: 0,
};

type HexMapTileMap = HexMapProperties['tiles'];

/**
 * Generate a map by composing a shape strategy and a noise strategy.
 *
 * Example:
 * `generateHexMap({
 *   shape: createCircularHexMapShape(10),
 *   noise: createRandomTileTypeNoise(),
 *   seed: roomId,
 * })`
 */
const generateHexMap: HexMapGenerator = ({
  shape,
  noise,
  seed,
  baseTile = DEFAULT_BASE_TILE,
}: GenerateHexMapInput): HexMapProperties => {
  const normalizedSeed = String(seed);
  const generator = createRandomGenerator(`${normalizedSeed}:${shape.id}:${noise.id}`);
  const tiles: HexMapTileMap = new Map();

  for (const coordinates of shape.getCoordinates()) {
    const sampledTile = noise.sample(coordinates, {
      generator,
      seed: normalizedSeed,
      shape,
    });

    tiles.set(getHexTileMapKey(coordinates), {
      coordinates,
      type: sampledTile.type ?? baseTile.type,
      resourceValue: sampledTile.resourceValue ?? baseTile.resourceValue,
    });
  }

  return { tiles };
};

export { generateHexMap };
